import React, { useState, useRef, useCallback } from "react";
import PropTypes from "prop-types";
import { makeStyles } from "@material-ui/core/styles";
import AttachFile from "@material-ui/icons/AttachFile";
import Close from "@material-ui/icons/Close";
import { EditorState, ContentState, convertToRaw } from "draft-js";
import draftToHtml from "draftjs-to-html";
import { Editor } from "react-draft-wysiwyg";
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";
import { ThreeDots } from "react-loader-spinner";
import { useTranslation } from "react-i18next";
import { useFeedback } from "./FeedbackContext";
import { commentFormStyles, editorToolbarConfig } from "./feedbackStyles";

const useStyles = makeStyles(commentFormStyles);

let _attachId = 500;

export default function FeedbackCommentForm({ topicId, clientColor }) {
  const classes = useStyles();
  const { t } = useTranslation();
  const { addComment, saving } = useFeedback();
  const [editorState, setEditorState] = useState(EditorState.createEmpty());
  const [attachments, setAttachments] = useState([]);
  const fileInputRef = useRef(null);

  const getHtml = useCallback(
    () => draftToHtml(convertToRaw(editorState.getCurrentContent())),
    [editorState],
  );

  const hasContent = useCallback(() => {
    return editorState.getCurrentContent().getPlainText().trim().length > 0;
  }, [editorState]);

  const handleSend = async () => {
    if (!hasContent() || saving) return;
    const html = getHtml();
    try {
      await addComment(
        topicId,
        html,
        attachments.map((a) => a.file),
      );
      setEditorState(
        EditorState.push(
          editorState,
          ContentState.createFromText(""),
          "remove-range",
        ),
      );
      setAttachments([]);
    } catch (err) {
      console.error("[Feedback] Failed to add comment", err);
    }
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    const newAttachments = files.map((f) => ({
      id: `attach-${++_attachId}`,
      name: f.name,
      size: f.size,
      file: f,
    }));
    setAttachments((prev) => [...prev, ...newAttachments]);
    e.target.value = "";
  };

  const removeAttachment = (id) => {
    setAttachments((prev) => prev.filter((a) => a.id !== id));
  };

  const formatSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const btnBg = clientColor || "#555";

  return (
    <div className={classes.formContainer}>
      <div className={classes.editorWrapper}>
        <Editor
          editorState={editorState}
          onEditorStateChange={setEditorState}
          toolbar={editorToolbarConfig}
          placeholder={t("feedback.write_comment")}
        />
      </div>

      <div className={classes.formFooter}>
        <div className={classes.attachSection}>
          <button
            className={classes.attachBtn}
            onClick={() => fileInputRef.current?.click()}
            type="button"
          >
            <AttachFile style={{ fontSize: "13px" }} />
            {t("feedback.attach")}
          </button>
          <input
            type="file"
            multiple
            ref={fileInputRef}
            style={{ display: "none" }}
            onChange={handleFileSelect}
          />
          {attachments.map((a) => (
            <span key={a.id} className={classes.attachedFile}>
              {a.name} ({formatSize(a.size)})
              <Close
                className={classes.removeAttach}
                onClick={() => removeAttachment(a.id)}
              />
            </span>
          ))}
        </div>

        <button
          className={classes.sendBtn}
          style={{ background: btnBg }}
          onClick={handleSend}
          disabled={!hasContent() || saving}
        >
          {saving ? (
            <ThreeDots
              height={12}
              width={20}
              color="#fff"
              ariaLabel="sending"
            />
          ) : (
            t("feedback.send")
          )}
        </button>
      </div>
    </div>
  );
}

FeedbackCommentForm.propTypes = {
  topicId: PropTypes.number.isRequired,
  clientColor: PropTypes.string,
};
